import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WorkLogModal = ({ isOpen, onClose, onSave, workLog }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (workLog) {
      console.log('Setting work log data:', workLog);
      setFormData({
        date: workLog.date ? new Date(workLog.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        hours: workLog.hours ? workLog.hours.toString() : '',
        description: workLog.description || ''
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        hours: '',
        description: ''
      });
    }
  }, [workLog]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.date || !formData.hours) {
      setError(t('workLogs.errors.requiredFields'));
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave({
        date: formData.date,
        hours: parseFloat(formData.hours),
        description: formData.description
      });
      onClose();
    } catch (err) {
      console.error('Error in WorkLogModal:', err);
      setError(err.message || t('workLogs.errors.saveWorkLog'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {workLog ? t('workLogs.editWorkLog') : t('workLogs.newWorkLog')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('workLogs.date')} *
            </label>
            <input
              type="date"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('workLogs.hours')} *
            </label>
            <input
              type="number"
              required
              step="0.5"
              min="0.5"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('workLogs.description')}
            </label>
            <textarea
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting 
                ? t('workLogs.saving') 
                : (workLog ? t('common.actions.update') : t('common.actions.save'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkLogModal;